import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Routes push-notification taps to the exact screen.
 *
 * Two paths:
 *  - App already open: the service worker focuses the tab and posts
 *    { type: "notification-navigate", url } — we push it into the router.
 *  - App closed: the SW opens the URL directly (deep link), and we strip the
 *    `?from=push` marker so the URL stays clean.
 */
const PushNotificationRouter = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== "notification-navigate" || typeof data.url !== "string") return;
      try {
        const target = new URL(data.url, window.location.origin);
        if (target.origin !== window.location.origin) return;
        navigate(target.pathname + target.search + target.hash);
      } catch {
        /* ignore malformed url */
      }
    };

    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [navigate]);

  // Cold-start deep link: remove the tracking marker without a reload.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("from") !== "push") return;
    params.delete("from");
    const search = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (search ? `?${search}` : "") + window.location.hash,
    );
  }, []);

  return null;
};

export default PushNotificationRouter;
